import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContactsRoundedIcon from "@mui/icons-material/ContactsRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  phone: "",
  sendPREmails: false,
};

const contactToFormData = (contact = {}) => ({
  firstName: contact.firstName || "",
  lastName: contact.lastName || "",
  email: contact.email || "",
  address1: contact.address1 || "",
  address2: contact.address2 || "",
  city: contact.city || "",
  state: contact.state || "",
  zip: contact.zip || "",
  country: contact.country || "",
  phone: contact.phone || "",
  sendPREmails: Boolean(contact.sendPREmails),
});

const cleanString = (value) =>
  typeof value === "string" ? value.trim() : "";

const contactDisplayName = (contact) => {
  const name = [contact.firstName, contact.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || contact.email || "Unnamed Contact";
};

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString();
};

export default function AdminContactsPage() {
  const { style } = useStyle();

  const token = useMemo(() => localStorage.getItem("token") || "", []);

  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [formData, setFormData] = useState(emptyForm);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const panelBg = style.darkPanel || "rgba(9, 12, 18, 0.45)";

  const sharedTextFieldSx = {
    width: "100%",
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: style.color2,
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: style.color3,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: style.accentGreen,
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "& .MuiFormHelperText-root": {
      color: style.color3,
      marginLeft: 0,
    },
  };

  const sectionCardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: "14px",
    backgroundColor: panelBg,
    border: "1px solid rgba(255,255,255,0.08)",
  };

  const selectedContact = useMemo(
    () => contacts.find((contact) => contact._id === selectedContactId) || null,
    [contacts, selectedContactId]
  );

  const filteredContacts = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!query) return contacts;

    return contacts.filter((contact) => {
      const searchableText = [
        contact.firstName,
        contact.lastName,
        contact.email,
        contact.phone,
        contact.city,
        contact.state,
        contact.country,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [contacts, searchText]);

  const loadContacts = async () => {
    if (!token) {
      toast.error("You must be logged in as an admin.");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post("/admin/get-all-contacts", { token });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      const contactList = Array.isArray(data) ? data : data.contacts || [];

      contactList.sort((a, b) => {
        const aName = `${a.lastName || ""} ${a.firstName || ""} ${a.email || ""}`;
        const bName = `${b.lastName || ""} ${b.firstName || ""} ${b.email || ""}`;
        return aName.localeCompare(bName);
      });

      setContacts(contactList);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Unable to load contacts."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    loadContacts();
  }, []);

  const handleChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSelectContact = (contact) => {
    setSelectedContactId(contact._id);
    setFormData(contactToFormData(contact));
  };

  const handleNewContact = () => {
    setSelectedContactId("");
    setFormData(emptyForm);
  };

  const buildContactPayload = () => ({
    firstName: cleanString(formData.firstName),
    lastName: cleanString(formData.lastName),
    email: cleanString(formData.email).toLowerCase(),
    address1: cleanString(formData.address1),
    address2: cleanString(formData.address2),
    city: cleanString(formData.city),
    state: cleanString(formData.state),
    zip: cleanString(formData.zip),
    country: cleanString(formData.country),
    phone: cleanString(formData.phone),
    sendPREmails: Boolean(formData.sendPREmails),
  });

  const handleSave = async () => {
    if (!token) {
      toast.error("You must be logged in as an admin.");
      return;
    }

    const contactData = buildContactPayload();

    if (!contactData.email) {
      toast.error("Email is required.");
      return;
    }

    try {
      setSaving(true);

      const endpoint = selectedContactId
        ? "/admin/update-contact"
        : "/admin/create-contact";

      const requestBody = selectedContactId
        ? {
            token,
            contactId: selectedContactId,
            contactData,
          }
        : {
            token,
            contactData,
          };

      const { data } = await axios.post(endpoint, requestBody);

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      const savedContact = data.contact || data;

      setContacts((previous) => {
        const exists = previous.some(
          (contact) => contact._id === savedContact._id
        );

        const nextContacts = exists
          ? previous.map((contact) =>
              contact._id === savedContact._id ? savedContact : contact
            )
          : [...previous, savedContact];

        return [...nextContacts].sort((a, b) => {
          const aName = `${a.lastName || ""} ${a.firstName || ""} ${a.email || ""}`;
          const bName = `${b.lastName || ""} ${b.firstName || ""} ${b.email || ""}`;
          return aName.localeCompare(bName);
        });
      });

      setSelectedContactId(savedContact._id);
      setFormData(contactToFormData(savedContact));

      toast.success(
        selectedContactId ? "Contact updated." : "Contact created."
      );
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.error;

      if (status === 409 || error?.response?.data?.code === 11000) {
        toast.error("A contact with that email address already exists.");
      } else {
        toast.error(message || "Unable to save contact.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedContactId) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${contactDisplayName(
        selectedContact || formData
      )}?`
    );

    if (!confirmed) return;

    try {
      setDeleting(true);

      const { data } = await axios.post("/admin/delete-contact", {
        token,
        contactId: selectedContactId,
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      setContacts((previous) =>
        previous.filter((contact) => contact._id !== selectedContactId)
      );
      setSelectedContactId("");
      setFormData(emptyForm);

      toast.success("Contact deleted.");
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Unable to delete contact."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contacts | Admin | Parish Audio"
        description="Manage Parish Audio contacts."
        canonical="https://parishaudio.com/admin/contacts"
        robots="noindex,nofollow"
      />

      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          color: style.color4,
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ maxWidth: 1450, mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2.25, md: 3 },
                backgroundColor: panelBg,
                borderBottom: `1px solid ${style.color2}`,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Stack spacing={1} alignItems="flex-start">
                  <Chip
                    icon={<ContactsRoundedIcon />}
                    label="Admin Contacts"
                    sx={{
                      borderRadius: "5px",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                    }}
                  />

                  <Typography
                    sx={{
                      color: style.color4,
                      fontSize: { xs: "1.9rem", md: "2.5rem" },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    Contact Manager
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
                    Create, search, edit, and delete press and business contacts.
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <Button
                    startIcon={<AddRoundedIcon />}
                    onClick={handleNewContact}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    New Contact
                  </Button>

                  <Button
                    startIcon={<RefreshRoundedIcon />}
                    onClick={loadContacts}
                    disabled={loading}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    Refresh
                  </Button>

                  <Button
                    startIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={saving || loading}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                    }}
                  >
                    {saving
                      ? "Saving..."
                      : selectedContactId
                      ? "Save Changes"
                      : "Create Contact"}
                  </Button>

                  <Button
                    startIcon={<DeleteRoundedIcon />}
                    onClick={handleDelete}
                    disabled={!selectedContactId || deleting}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#ffb4b4",
                      backgroundColor: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={sectionCardSx}>
                    <Stack spacing={1.5}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          sx={{ color: style.color4, fontWeight: 800 }}
                        >
                          Contacts
                        </Typography>

                        <Chip
                          label={`${filteredContacts.length} shown`}
                          size="small"
                          sx={{
                            color: style.color3,
                            backgroundColor: "rgba(255,255,255,0.05)",
                          }}
                        />
                      </Stack>

                      <TextField
                        label="Search contacts"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        size="small"
                        sx={sharedTextFieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchRoundedIcon
                                sx={{ color: style.color3 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Divider
                        sx={{ borderColor: "rgba(255,255,255,0.08)" }}
                      />

                      <Stack
                        spacing={1}
                        sx={{
                          maxHeight: { xs: 420, md: 720 },
                          overflowY: "auto",
                          pr: 0.5,
                        }}
                      >
                        {loading ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ py: 4 }}
                          >
                            <CircularProgress
                              size={22}
                              sx={{ color: style.accentGreen }}
                            />
                            <Typography sx={{ color: style.color3 }}>
                              Loading contacts...
                            </Typography>
                          </Stack>
                        ) : filteredContacts.length === 0 ? (
                          <Typography
                            sx={{
                              color: style.color3,
                              textAlign: "center",
                              py: 4,
                            }}
                          >
                            No contacts found.
                          </Typography>
                        ) : (
                          filteredContacts.map((contact) => {
                            const isSelected =
                              contact._id === selectedContactId;

                            return (
                              <Paper
                                key={contact._id}
                                elevation={0}
                                onClick={() => handleSelectContact(contact)}
                                sx={{
                                  p: 1.5,
                                  cursor: "pointer",
                                  borderRadius: "10px",
                                  backgroundColor: isSelected
                                    ? "rgba(255,255,255,0.09)"
                                    : "rgba(255,255,255,0.03)",
                                  border: isSelected
                                    ? `1px solid ${style.accentGreen}`
                                    : "1px solid rgba(255,255,255,0.08)",
                                  transition:
                                    "background-color 120ms ease, border-color 120ms ease",
                                  "&:hover": {
                                    backgroundColor:
                                      "rgba(255,255,255,0.07)",
                                  },
                                }}
                              >
                                <Stack spacing={0.6}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    spacing={1}
                                  >
                                    <Typography
                                      sx={{
                                        color: style.color4,
                                        fontWeight: 800,
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      {contactDisplayName(contact)}
                                    </Typography>

                                    {contact.sendPREmails && (
                                      <Chip
                                        label="PR"
                                        size="small"
                                        sx={{
                                          height: 22,
                                          color: style.color4,
                                          fontWeight: 700,
                                          backgroundColor: style.accentGreen,
                                        }}
                                      />
                                    )}
                                  </Stack>

                                  {contact.email && (
                                    <Stack
                                      direction="row"
                                      spacing={0.75}
                                      alignItems="center"
                                    >
                                      <EmailRoundedIcon
                                        sx={{
                                          color: style.color3,
                                          fontSize: 17,
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          color: style.color3,
                                          fontSize: "0.9rem",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {contact.email}
                                      </Typography>
                                    </Stack>
                                  )}

                                  {contact.phone && (
                                    <Typography
                                      sx={{
                                        color: style.color3,
                                        fontSize: "0.88rem",
                                      }}
                                    >
                                      {contact.phone}
                                    </Typography>
                                  )}

                                  {(contact.city ||
                                    contact.state ||
                                    contact.country) && (
                                    <Typography
                                      sx={{
                                        color: style.color3,
                                        fontSize: "0.82rem",
                                      }}
                                    >
                                      {[contact.city, contact.state, contact.country]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </Typography>
                                  )}
                                </Stack>
                              </Paper>
                            );
                          })
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Stack spacing={2}>
                    <Paper elevation={0} sx={sectionCardSx}>
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 800 }}
                            >
                              {selectedContactId
                                ? "Edit Contact"
                                : "New Contact"}
                            </Typography>

                            <Typography
                              sx={{
                                color: style.color3,
                                fontSize: "0.9rem",
                                mt: 0.25,
                              }}
                            >
                              {selectedContactId
                                ? "Update the selected contact's information."
                                : "Enter the contact information and click Create Contact."}
                            </Typography>
                          </Box>

                          {selectedContact && (
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={0.75}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                            >
                              {selectedContact.createdAt && (
                                <Chip
                                  label={`Created: ${formatDate(
                                    selectedContact.createdAt
                                  )}`}
                                  size="small"
                                  sx={{
                                    color: style.color3,
                                    backgroundColor:
                                      "rgba(255,255,255,0.05)",
                                  }}
                                />
                              )}

                              {selectedContact.updatedAt && (
                                <Chip
                                  label={`Updated: ${formatDate(
                                    selectedContact.updatedAt
                                  )}`}
                                  size="small"
                                  sx={{
                                    color: style.color3,
                                    backgroundColor:
                                      "rgba(255,255,255,0.05)",
                                  }}
                                />
                              )}
                            </Stack>
                          )}
                        </Stack>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="First Name"
                              value={formData.firstName}
                              onChange={(event) =>
                                handleChange("firstName", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Last Name"
                              value={formData.lastName}
                              onChange={(event) =>
                                handleChange("lastName", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} md={7}>
                            <TextField
                              label="Email"
                              type="email"
                              required
                              value={formData.email}
                              onChange={(event) =>
                                handleChange("email", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} md={5}>
                            <TextField
                              label="Phone"
                              value={formData.phone}
                              onChange={(event) =>
                                handleChange("phone", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Paper>

                    <Paper elevation={0} sx={sectionCardSx}>
                      <Stack spacing={1.5}>
                        <Typography
                          sx={{ color: style.color4, fontWeight: 800 }}
                        >
                          Address
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label="Address Line 1"
                              value={formData.address1}
                              onChange={(event) =>
                                handleChange("address1", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="Address Line 2"
                              value={formData.address2}
                              onChange={(event) =>
                                handleChange("address2", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="City"
                              value={formData.city}
                              onChange={(event) =>
                                handleChange("city", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="State / Region"
                              value={formData.state}
                              onChange={(event) =>
                                handleChange("state", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="ZIP / Postal Code"
                              value={formData.zip}
                              onChange={(event) =>
                                handleChange("zip", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="Country"
                              value={formData.country}
                              onChange={(event) =>
                                handleChange("country", event.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Paper>

                    <Paper elevation={0} sx={sectionCardSx}>
                      <Stack spacing={1}>
                        <Typography
                          sx={{ color: style.color4, fontWeight: 800 }}
                        >
                          Email Preferences
                        </Typography>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.sendPREmails}
                              onChange={(event) =>
                                handleChange(
                                  "sendPREmails",
                                  event.target.checked
                                )
                              }
                              sx={{
                                color: style.color3,
                                "&.Mui-checked": {
                                  color: style.accentGreen,
                                },
                              }}
                            />
                          }
                          label="Include this contact in press release emails"
                          sx={{ color: style.color4 }}
                        />

                        <Typography
                          sx={{
                            color: style.color3,
                            fontSize: "0.88rem",
                            lineHeight: 1.6,
                          }}
                        >
                          This flag can be used when selecting recipients for
                          product announcements and press releases.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}
